import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';

import cityStore from '../../../../store/cities.store';

type Severity = 'success' | 'error';

type FieldErrors = Partial<{
  subdomain: string;
  name: string;
  name_rod: string;
  name_pred: string;
  phone: string;
  email: string;
  address: string;
}>;

const City = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();

  const [subdomain, setSubdomain] = useState('');
  const [name, setName] = useState('');
  const [nameRod, setNameRod] = useState('');
  const [namePred, setNamePred] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<Severity>('success');
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (!isNew && id) {
      cityStore.loadOne(id).then(() => {
        const c = cityStore.current;
        if (c) {
          setSubdomain(c.subdomain || '');
          setName(c.name || '');
          setNameRod(c.name_rod || '');
          setNamePred(c.name_pred || '');
          setPhone(c.phone || '');
          setEmail(c.email || '');
          setAddress(c.address || '');
        }
      });
    } else {
      cityStore.resetCurrent();
      setSubdomain('');
      setName('');
      setNameRod('');
      setNamePred('');
      setPhone('');
      setEmail('');
      setAddress('');
    }
    setErrors({});
  }, [id, isNew]);

  const openSnack = (message: string, severity: Severity) => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleChange =
    (field: keyof FieldErrors, setter: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      if (errors[field]) {
        setErrors({ ...errors, [field]: undefined });
      }
    };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      const payload = {
        subdomain: subdomain.trim().toLowerCase(),
        name: name.trim(),
        name_rod: nameRod.trim(),
        name_pred: namePred.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim()
      };

      if (isNew) {
        const created = await cityStore.create(payload);
        openSnack('Город создан', 'success');
        if (created) {
          navigate(`/cities/${created.id}`);
        }
      } else if (id) {
        await cityStore.update(id, payload);
        openSnack('Изменения сохранены', 'success');
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const data = err.response?.data as
          | { message?: string; errors?: Record<string, string[]> }
          | undefined;

        if (status === 422 && data?.errors) {
          const mapped: FieldErrors = {};
          Object.keys(data.errors).forEach((k) => {
            const first = data.errors?.[k]?.[0];
            if (first) mapped[k as keyof FieldErrors] = first;
          });
          setErrors(mapped);
          openSnack('Исправьте ошибки в форме', 'error');
        } else {
          const msg = data?.message || err.message || 'Не удалось сохранить';
          openSnack(msg, 'error');
        }
      } else {
        const msg = (err as Error)?.message || 'Не удалось сохранить';
        openSnack(msg, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3} maxWidth={640} component="form" onSubmit={onSubmit}>
      <Typography variant="h5" mb={3}>
        {isNew ? 'Добавить город' : 'Редактировать город'}
      </Typography>

      <TextField
        label="Поддомен"
        value={subdomain}
        onChange={handleChange('subdomain', setSubdomain)}
        required
        fullWidth
        margin="normal"
        placeholder="например: msk"
        error={Boolean(errors.subdomain)}
        helperText={errors.subdomain}
      />
      <TextField
        label="Название"
        value={name}
        onChange={handleChange('name', setName)}
        required
        fullWidth
        margin="normal"
        error={Boolean(errors.name)}
        helperText={errors.name}
      />
      <TextField
        label="Город (родительный)"
        value={nameRod}
        onChange={handleChange('name_rod', setNameRod)}
        required
        fullWidth
        margin="normal"
        error={Boolean(errors.name_rod)}
        helperText={errors.name_rod}
      />
      <TextField
        label="Город (предложный)"
        value={namePred}
        onChange={handleChange('name_pred', setNamePred)}
        required
        fullWidth
        margin="normal"
        error={Boolean(errors.name_pred)}
        helperText={errors.name_pred}
      />
      <TextField
        label="Телефон"
        value={phone}
        onChange={handleChange('phone', setPhone)}
        fullWidth
        margin="normal"
        error={Boolean(errors.phone)}
        helperText={errors.phone}
      />
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={handleChange('email', setEmail)}
        fullWidth
        margin="normal"
        error={Boolean(errors.email)}
        helperText={errors.email}
      />
      <TextField
        label="Адрес"
        value={address}
        onChange={handleChange('address', setAddress)}
        fullWidth
        margin="normal"
        error={Boolean(errors.address)}
        helperText={errors.address}
      />

      <Button type="submit" variant="contained" disabled={saving}>
        {saving ? 'Сохранение…' : 'Сохранить'}
      </Button>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          sx={{ width: '100%' }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default observer(City);
